import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Table } from '@cloudflare/kumo/components/table';
import {
  DialogRoot,
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@cloudflare/kumo/components/dialog';
import { ArrowCounterClockwise, EnvelopeSimple, Bell } from '@phosphor-icons/react';
import { api } from '@/lib/api';

interface BillingBreakdown {
  venue: number;
  equipment: number;
  power: number;
  sponsor: number;
  sponsorUnits: number;
  sponsorDisplayName: string | null;
  totalExhibitFee: number;
  grandTotal: number;
  finalUsed: boolean;
  isFree: boolean;
}

interface PendingRow {
  id: string;
  applicationNumber: number;
  email: string;
  realName: string;
  exhibitorName: string;
  exhibitTitle: string;
  type: string;
  status: string;
  paymentNotificationSent: boolean;
  paymentNotificationSentAt: string | null;
  paidAt: string | null;
  paymentMethod: string | null;
  billing: BillingBreakdown;
  // 実際に決済された額 (payments テーブル)。billing は現在の申込内容からの再計算値なので、
  // 支払い後に料金 override を変えると実額とズレる。null は payments 行が無いケース
  // (全額無料で Stripe をスキップ / 銀行振込等で運営が手動で paid にした)。
  paidAmount: number | null;
}

interface WaitingRow extends PendingRow {}
interface PaidRow extends PendingRow {}

const TYPE_LABEL: Record<string, string> = {
  general: '一般',
  'student-university': '学生 (大学)',
  'student-high-school': '学生 (高校以下)',
  corporate: '企業',
};

const yen = (n: number) => `${(n ?? 0).toLocaleString('ja-JP')} 円`;

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('ja-JP', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short',
    });
  } catch {
    return iso;
  }
}

export default function PaymentsPage() {
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [waiting, setWaiting] = useState<WaitingRow[]>([]);
  const [paid, setPaid] = useState<PaidRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewing, setPreviewing] = useState<{
    eligibleCount: number;
    skippedCount: number;
    dueDate: string;
  } | null>(null);
  const [sending, setSending] = useState(false);
  const [reminderId, setReminderId] = useState<string | null>(null);

  const selectedCount = selectedIds.size;
  const allChecked = pending.length > 0 && selectedCount === pending.length;
  const anyChecked = selectedCount > 0;

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pendingRes, listRes] = await Promise.all([
        api.get<{ data: PendingRow[] }>('/api/admin/exhibitors/pending-payment-notification'),
        api.get<{ data: PendingRow[]; pagination?: unknown }>(
          '/api/admin/exhibitors?year=2026&limit=500&includeCancelled=false'
        ),
      ]);
      setPending(pendingRes.data.data ?? []);

      const all = listRes.data.data ?? [];
      // 一覧 API が billing を返す (2026 のみ)。返らなかったときの保険として pending 側の
      // billing を id でマージし、それも無ければ空で詰める。
      const billingMap = new Map((pendingRes.data.data ?? []).map((r) => [r.id, r.billing]));
      const augmented = all.map((row) => ({
        ...row,
        billing: row.billing ?? billingMap.get(row.id) ?? emptyBilling(),
        paidAmount: row.paidAmount ?? null,
      }));
      setWaiting(
        augmented.filter(
          (r) => r.status === 'waitingForPay' && r.paymentNotificationSent && !r.paidAt
        )
      );
      setPaid(augmented.filter((r) => r.status === 'paid'));
    } catch (e: unknown) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pending.map((r) => r.id)));
    }
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openPreview = async () => {
    if (selectedCount === 0) return;
    setError(null);
    try {
      const res = await api.post('/api/admin/exhibitors/send-payment-notifications', {
        exhibitorIds: Array.from(selectedIds),
        dryRun: true,
      });
      const data = (res.data?.data ?? res.data) as {
        eligibleCount: number;
        skippedCount: number;
        dueDate: string;
      };
      setPreviewing({
        eligibleCount: data.eligibleCount,
        skippedCount: data.skippedCount,
        dueDate: data.dueDate,
      });
    } catch (e) {
      setError(extractError(e));
    }
  };

  const confirmSend = async () => {
    if (!previewing) return;
    setSending(true);
    setError(null);
    try {
      const res = await api.post('/api/admin/exhibitors/send-payment-notifications', {
        exhibitorIds: Array.from(selectedIds),
        dryRun: false,
      });
      const data = (res.data?.data ?? res.data) as {
        queued: number;
        failed: Array<{ exhibitorId: string; reason: string }>;
        dueDate: string;
      };
      setInfo(
        `請求メールを ${data.queued} 件キューに登録しました（毎分順次送信されます）${
          data.failed?.length ? ` / スキップ ${data.failed.length} 件` : ''
        }。支払期限: ${formatDate(data.dueDate)}`
      );
      setSelectedIds(new Set());
      setPreviewing(null);
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSending(false);
    }
  };

  const sendReminder = async (id: string) => {
    setReminderId(id);
    setError(null);
    try {
      await api.post(`/api/admin/exhibitors/${id}/send-payment-reminder`);
      setInfo('リマインドを送信しました。');
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setReminderId(null);
    }
  };

  // 請求予定セクション専用。これは「今回の請求に上乗せする分」であって支援の実績ではない
  // (申込時の仮値ベースで、後追いの追加支援 addon を含まない)。実績は /sponsorships 参照。
  const sponsorBadge = (b: BillingBreakdown) =>
    b.sponsorUnits > 0 ? (
      <Badge variant="primary">今回請求分 スポンサー {b.sponsorUnits} 口</Badge>
    ) : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-kumo-default">支払い管理</h1>
          <p className="text-sm text-kumo-muted">
            料金が確定し「支払い待ち」状態の出展者に請求メールを送信します。
            ステータス遷移自体は出展者詳細画面の「ステータス」エディタから行ってください。
          </p>
        </div>
        <Button
          variant="secondary"
          icon={ArrowCounterClockwise}
          onClick={refresh}
          loading={loading}
        >
          再読み込み
        </Button>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      <Banner variant="alert">
        <strong>登録後の取り消しはできません。</strong>{' '}
        「送信する」を押すと選択中の出展者全員が送信キューに登録され、毎分順次メールが届きます。請求額に誤りが無いか送信前に必ずご確認ください。
      </Banner>

      {/* ─── 未通知 ─── */}
      <Surface>
        <div className="flex items-center justify-between border-b border-kumo-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-kumo-default">
              未通知 ({pending.length})
            </h2>
            <p className="text-xs text-kumo-muted">
              「支払い待ち」かつ請求メール未送信の出展者。チェックを入れて一括送信できます。
              支払い期限は送信ボタンを押した時点で決まり、確認ダイアログに表示されます
              (送信日から 4 日後)。
            </p>
          </div>
          <Button
            icon={EnvelopeSimple}
            disabled={!anyChecked || loading}
            onClick={openPreview}
          >
            選択した {selectedCount} 件に請求メール送信
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader />
          </div>
        ) : pending.length === 0 ? (
          <Empty title="未通知の出展者はありません" />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    aria-label="すべて選択"
                  />
                </Table.Head>
                <Table.Head>出展者番号</Table.Head>
                <Table.Head>出展者</Table.Head>
                <Table.Head>
                  <span title="一般 / 大学生 / 高校生以下 / 企業 のいずれか (申込フォームで選択)">
                    形態
                  </span>
                </Table.Head>
                <Table.Head>出展タイトル</Table.Head>
                <Table.Head className="text-right">請求総額 (税込)</Table.Head>
                <Table.Head>備考</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pending.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                      aria-label={`${row.applicationNumber} を選択`}
                    />
                  </Table.Cell>
                  <Table.Cell>#{row.applicationNumber}</Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="text-sm text-kumo-default">{row.exhibitorName}</span>
                      <span className="text-xs text-kumo-muted">{row.email}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>{TYPE_LABEL[row.type] ?? row.type}</Table.Cell>
                  <Table.Cell>{row.exhibitTitle}</Table.Cell>
                  <Table.Cell
                    className="text-right font-mono"
                    title={`会場費 ${yen(row.billing.venue)} + 備品費 ${yen(row.billing.equipment)} + 電源費 ${yen(row.billing.power)}${row.billing.sponsorUnits > 0 ? ` + 少額スポンサー ${yen(row.billing.sponsor)} (${row.billing.sponsorUnits} 口)` : ''}`}
                  >
                    {yen(row.billing.grandTotal)}
                    {row.billing.finalUsed && (
                      <Badge variant="outline" className="ml-2">
                        override
                      </Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>{sponsorBadge(row.billing)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      {/* ─── 送信済 (支払い待ち) ─── */}
      <Surface>
        <div className="border-b border-kumo-border px-4 py-3">
          <h2 className="text-base font-semibold text-kumo-default">
            送信済・支払い待ち ({waiting.length})
          </h2>
          <p className="text-xs text-kumo-muted">
            請求メール送信済みでまだ入金されていない出展者。期限が近づいたらリマインドを送ってください。
          </p>
        </div>
        {waiting.length === 0 ? (
          <Empty title="支払い待ちの出展者はいません" />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>出展者番号</Table.Head>
                <Table.Head>出展者</Table.Head>
                <Table.Head>合計</Table.Head>
                <Table.Head>最後の送信</Table.Head>
                <Table.Head>操作</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {waiting.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>#{row.applicationNumber}</Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="text-sm text-kumo-default">{row.exhibitorName}</span>
                      <span className="text-xs text-kumo-muted">{row.email}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="font-mono">{yen(row.billing.grandTotal)}</Table.Cell>
                  <Table.Cell>{formatDateTime(row.paymentNotificationSentAt)}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={Bell}
                      loading={reminderId === row.id}
                      onClick={() => sendReminder(row.id)}
                    >
                      リマインド送信
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      {/* ─── 支払い完了済 ─── */}
      <Surface>
        <div className="border-b border-kumo-border px-4 py-3">
          <h2 className="text-base font-semibold text-kumo-default">
            支払い完了済 ({paid.length})
          </h2>
          {/* 少額スポンサーの列は置かない。ここに出せるのは申込時の仮値だけで、後追いの
              追加支援 (addon) を含まないため実績として誤読される。実績は /sponsorships を見る。 */}
          <p className="mt-1 text-xs text-kumo-muted">
            少額スポンサーの入金実績は{' '}
            <Link to="/sponsorships" className="underline">
              少額スポンサー
            </Link>{' '}
            ページで確認してください (この表には含まれません)。
          </p>
        </div>
        {paid.length === 0 ? (
          <Empty title="完了済はまだありません" />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>出展者番号</Table.Head>
                <Table.Head>出展者</Table.Head>
                <Table.Head className="text-right">決済額</Table.Head>
                <Table.Head>支払日時</Table.Head>
                <Table.Head>方法</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paid.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>#{row.applicationNumber}</Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="text-sm text-kumo-default">{row.exhibitorName}</span>
                      <span className="text-xs text-kumo-muted">{row.email}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-right font-mono">
                    {row.paidAmount !== null ? (
                      yen(row.paidAmount)
                    ) : (
                      // payments 行が無い。再計算値を出すが、実額の保証が無いことを明示する。
                      <span title="決済記録が無いため、現在の申込内容から再計算した参考値です">
                        {yen(row.billing.grandTotal)}
                        <Badge variant="outline" className="ml-2">
                          参考値
                        </Badge>
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>{formatDateTime(row.paidAt)}</Table.Cell>
                  <Table.Cell>
                    <Badge variant="secondary">{row.paymentMethod ?? '-'}</Badge>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      <DialogRoot
        role="alertdialog"
        open={previewing !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewing(null);
        }}
      >
        <Dialog className="flex flex-col gap-4 p-6">
          <DialogTitle>請求メールを送信しますか?</DialogTitle>
          <DialogDescription>
            {previewing && (
              <>
                対象 <strong>{previewing.eligibleCount}</strong> 件を送信キューに登録します（毎分順次送信されます）。
                <strong>登録後に取り消すことはできません。</strong>
                {previewing.skippedCount > 0 && (
                  <>
                    <br />
                    条件を満たさない {previewing.skippedCount} 件はスキップされます。
                  </>
                )}
                <br />
                請求書に記載する支払期限: <strong>{formatDate(previewing.dueDate)}</strong>
              </>
            )}
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <DialogClose
              render={(p) => (
                <Button {...p} variant="secondary">
                  キャンセル
                </Button>
              )}
            />
            <Button onClick={confirmSend} loading={sending} icon={EnvelopeSimple}>
              送信する
            </Button>
          </div>
        </Dialog>
      </DialogRoot>
    </div>
  );
}

function emptyBilling(): BillingBreakdown {
  return {
    venue: 0,
    equipment: 0,
    power: 0,
    sponsor: 0,
    sponsorUnits: 0,
    sponsorDisplayName: null,
    totalExhibitFee: 0,
    grandTotal: 0,
    finalUsed: false,
    isFree: false,
  };
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '送信に失敗しました';
  }
  return e instanceof Error ? e.message : '送信に失敗しました';
}
